import React, { useContext, useLayoutEffect } from 'react';
import { graphql, Link } from 'gatsby';
import { css } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FullImage from '../components/full-image';
import Button from '../components/button';
import PortableText from '../components/portable-text';
import PDF from '../components/pdf';
import Gallery from '../components/gallery';
import Accordion from '../components/accordion';

export default function Hire({ data }) {
  const theme = useContext(ThemeContext);
  const tourEmbed = data.hire.buildingEmbed?.replace(/<iframe\b([^>]*)>/gi, (match, attributes) => {
    const attributeNames = attributes.replace(/(["'])[\s\S]*?\1/g, '');
    return `<iframe${attributes}${/(?:^|\s)title\s*=/i.test(attributeNames) ? '' : ' title="3D tour of The Substation"'}${/(?:^|\s)loading\s*=/i.test(attributeNames) ? '' : ' loading="lazy"'}>`;
  });

  useLayoutEffect(() => {
    theme.setGrayFooter(true);
  }, []);

  function dateDiff(d1, d2) {
    return (d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24);
  }

  return (
    <div
      css={css`
        background-color: var(--white);
      `}
    >
      <SEO
        title="Hire"
        description={(data.hire._rawContent) ? data.hire._rawContent.map(el => el.children).flat().map((el) => el?.text)[0] : null}
        imageID={(data.hire.featureImage) ? data.hire.featureImage.asset._id : null}
      />
      <div
        css={css`
          background-color: var(--primary);
        `}
      >
        <FullImage
          text={`
            Hire/
          `}
          image={data.hire.featureImage}
          includeTitle
        />
      </div>
      <div
        css={css`
          margin: 0 var(--margin);
        `}
      >
        <div
          className="type--45"
          css={css`
            margin-bottom: var(--bigMargin);
          `}
        >
          <PortableText
            blocks={data.hire._rawContent}
          />
        </div>
        <div
          className="type--45"
          css={css`
            margin-bottom: var(--bigMargin);
          `}
        >
          <div
            css={css`
              margin-bottom: var(--bigMargin);
            `}
          >
            <PortableText
              blocks={data.hire._rawHireText}
            />
          </div>
          <div
            css={css`
              margin-bottom: var(--margin);
            `}
          >
            {data.hire.venues.length > 0 && data.hire.venues
              .map((venue, i) => (
                <div
                  css={css`
                    position: relative;
                    height: 90vh;
                    margin-bottom: var(--margin);
                  `}
                >
                  {venue.featureImage?.asset && <Image
                    {...venue.featureImage}
                    width={1980}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    loading="eager"
                  />}
                  <div
                    css={css`
                      display: grid;
                      grid-template-columns: repeat(12, 1fr);
                      gap: var(--gutter);
                      position: absolute;
                      right: var(--margin);
                      bottom: var(--margin);
                      left: var(--margin);
                      text-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.2);
                    `}
                  >
                    <div
                      css={css`
                        grid-column: span 12;
                        color: var(--white);
                        margin-bottom: var(--gutter);
                      `}
                    >
                      <span css={css`text-transform: uppercase;`}>{venue.title}</span>: {venue.suitability}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        grid-column: span 6;
                        color: var(--primary);

                        @media (max-width: 500px) {
                          grid-column: span 12;
                        }
                      `}
                    >
                      <div>
                        <img
                          css={css`
                            width: 9.6rem;
                            margin-right: var(--gutter);
                          `}
                          src="/images/person.svg"
                        />
                      </div>
                      <div
                        css={css`
                          @media (max-width: 700px) {
                            position: relative;
                            top: 0.8rem;
                          }
                        `}
                      >
                        {venue.capacity}
                      </div>
                    </div>
                    <div
                      css={css`
                        display: flex;
                        grid-column: span 6;
                        color: var(--primary);

                        @media (max-width: 500px) {
                          grid-column: span 12;
                        }
                      `}
                    >
                      <div>
                        <img
                          css={css`
                            width: 9.6rem;
                            margin-right: var(--gutter);
                          `}
                          src="/images/x.svg"
                        />
                      </div>
                      <div
                        css={css`
                          @media (max-width: 700px) {
                            position: relative;
                            top: 0.8rem;
                          }
                        `}
                      >
                        {venue.dimensions}
                      </div>
                    </div>
                    <div
                      className="type--20"
                      css={css`
                        grid-column: span 12;
                        margin-top: var(--gutter);

                        @media (max-width: 500px) {
                          grid-column: span 12;
                        }

                        a {
                          color: var(--primary);
                          text-decoration: none;

                          &:hover {
                            color: var(--white);
                          }
                        }
                      `}
                    >
                      <Link
                        to={`/venue/${venue.slug.current}`}
                      >
                        View venue
                      </Link>
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
          <div>
            <Button
              css={css`
                margin: var(--margin);
                margin-bottom: 0;

                a {
                  margin-bottom: 0;
                }
              `}
              text="Enquire Now"
              link={data.hire.hireLink}
              large
              external
            />
          </div>
          {data.hire.buildingEmbed && <div
            className="type--20"
            css={css`
              margin-top: var(--margin);
              iframe {
                display: block;
                width: 100%;
                height: 66.66vh;
                border: 0;
              }
            `}
          >
            <div dangerouslySetInnerHTML={{__html: tourEmbed}} />
          </div>}
        </div>
      </div>
    </div>
  );
}

export const query = graphql`
  query {
    hire: sanityHire(_id: { regex: "/(drafts\\\\.)?hire-2024/" }) {
      id
      featureImage {
        ...Image
        asset {
          description
        }
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      venues {
        title
        slug {
          current
        }
        featureImage {
          ...Image
        }
        suitability
        capacity
        dimensions
      }
      hireLink
      buildingEmbed
    }
  }
`
